import type { OrderRecord } from '@adiox/api';
import type { Position } from '../stream/accountModel';
import { sideOfOrderType, type OrderType, type Side } from '../trading/tradeRules';

export type LineKind = 'position' | 'positionSl' | 'positionTp' | 'order' | 'orderSl' | 'orderTp';

/** A horizontal line the chart draws for something on the account. */
export interface ChartLine {
  /** Stable across ticks, so the chart updates a line instead of recreating it. */
  id: string;
  kind: LineKind;
  ticket: number;
  price: number;
  side: Side;
  title: string;
}

export interface SymbolPrecision {
  digits: number;
  tickSize: number;
}

const roundTo = (price: number, digits: number) => Number(price.toFixed(digits));

/** Snaps a price to the symbol's tick and decimals, so what the user sees is exactly what is sent. */
export function snapPrice(price: number, info: SymbolPrecision): number {
  const tick = info.tickSize > 0 ? info.tickSize : 1 / 10 ** info.digits;
  return roundTo(Math.round(price / tick) * tick, info.digits);
}

/** The lines for one symbol: each position with its stop levels, each pending order with its stop levels. */
export function linesFor(symbol: string, positions: Position[], orders: OrderRecord[]): ChartLine[] {
  const lines: ChartLine[] = [];
  for (const p of positions.filter((x) => x.symbol === symbol)) {
    const side = p.side as Side;
    const tag = `#${p.ticket}`;
    lines.push({
      id: `p${p.ticket}`,
      kind: 'position',
      ticket: p.ticket,
      price: p.priceOpen,
      side,
      title: `${tag} ${side.toLowerCase()} ${p.volume}${p.profit === null ? '' : ` · ${p.profit > 0 ? '+' : ''}${p.profit.toFixed(2)}`}`,
    });
    if (p.stopLoss > 0)
      lines.push({
        id: `p${p.ticket}sl`,
        kind: 'positionSl',
        ticket: p.ticket,
        price: p.stopLoss,
        side,
        title: `SL ${tag}`,
      });
    if (p.takeProfit > 0)
      lines.push({
        id: `p${p.ticket}tp`,
        kind: 'positionTp',
        ticket: p.ticket,
        price: p.takeProfit,
        side,
        title: `TP ${tag}`,
      });
  }
  for (const o of orders.filter((x) => x.symbol === symbol && x.status === 'Placed')) {
    const side = sideOfOrderType(o.type);
    const tag = `#${o.ticket}`;
    lines.push({
      id: `o${o.ticket}`,
      kind: 'order',
      ticket: o.ticket,
      price: o.priceOrder,
      side,
      title: `${tag} ${o.type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()} ${o.volumeCurrent}`,
    });
    if (o.stopLoss > 0)
      lines.push({
        id: `o${o.ticket}sl`,
        kind: 'orderSl',
        ticket: o.ticket,
        price: o.stopLoss,
        side,
        title: `SL ${tag}`,
      });
    if (o.takeProfit > 0)
      lines.push({
        id: `o${o.ticket}tp`,
        kind: 'orderTp',
        ticket: o.ticket,
        price: o.takeProfit,
        side,
        title: `TP ${tag}`,
      });
  }
  return lines;
}

export type DragOutcome =
  | { action: 'modifyPosition'; ticket: number; stopLoss: number; takeProfit: number; what: string }
  | {
      action: 'modifyOrder';
      ticket: number;
      price: number;
      stopLoss: number;
      takeProfit: number;
      what: string;
    }
  | { action: 'none'; reason: string };

/**
 * What releasing a dragged line at `dropped` means. SL and TP lines move their own level; a position's open-price line is
 * the MT5 gesture of pulling a stop out of it: dropped on the loss side it sets the stop loss, on the profit side the take
 * profit. A pending order's line moves the order's price. Levels not being changed are sent back as they are (the server
 * treats an omitted level as "remove it").
 */
export function resolveDrag(
  line: ChartLine,
  dropped: number,
  positions: Position[],
  orders: OrderRecord[],
  info: SymbolPrecision,
): DragOutcome {
  const price = snapPrice(dropped, info);
  if (!(price > 0)) return { action: 'none', reason: 'That price is not valid.' };
  if (price === snapPrice(line.price, info) && line.kind !== 'position')
    return { action: 'none', reason: 'unchanged' };

  if (line.kind === 'position' || line.kind === 'positionSl' || line.kind === 'positionTp') {
    const p = positions.find((x) => x.ticket === line.ticket);
    if (!p) return { action: 'none', reason: 'That position is no longer open.' };
    if (line.kind === 'positionSl')
      return {
        action: 'modifyPosition',
        ticket: p.ticket,
        stopLoss: price,
        takeProfit: p.takeProfit,
        what: 'Stop loss',
      };
    if (line.kind === 'positionTp')
      return {
        action: 'modifyPosition',
        ticket: p.ticket,
        stopLoss: p.stopLoss,
        takeProfit: price,
        what: 'Take profit',
      };
    if (price === snapPrice(p.priceOpen, info)) return { action: 'none', reason: 'unchanged' };
    const profitSide = p.side === 'Buy' ? price > p.priceOpen : price < p.priceOpen;
    return profitSide
      ? {
          action: 'modifyPosition',
          ticket: p.ticket,
          stopLoss: p.stopLoss,
          takeProfit: price,
          what: 'Take profit',
        }
      : {
          action: 'modifyPosition',
          ticket: p.ticket,
          stopLoss: price,
          takeProfit: p.takeProfit,
          what: 'Stop loss',
        };
  }

  const o = orders.find((x) => x.ticket === line.ticket);
  if (!o) return { action: 'none', reason: 'That order no longer exists.' };
  const same = { stopLoss: o.stopLoss, takeProfit: o.takeProfit };
  if (line.kind === 'orderSl')
    return {
      action: 'modifyOrder',
      ticket: o.ticket,
      price: o.priceOrder,
      ...same,
      stopLoss: price,
      what: 'Stop loss',
    };
  if (line.kind === 'orderTp')
    return {
      action: 'modifyOrder',
      ticket: o.ticket,
      price: o.priceOrder,
      ...same,
      takeProfit: price,
      what: 'Take profit',
    };
  return { action: 'modifyOrder', ticket: o.ticket, price, ...same, what: 'Order price' };
}

/** What the read-out shows while a line is dragged; money comes from the server's profit preview, never from here. */
export interface DragReadout {
  label: string;
  price: number;
  /** Signed distance from the reference price in points. */
  points: number;
}

/** The price a dragged level is measured from: a position's open price, or an order's own price. */
export function referencePrice(line: ChartLine, positions: Position[], orders: OrderRecord[]): number | null {
  if (line.kind === 'position' || line.kind === 'positionSl' || line.kind === 'positionTp') {
    return positions.find((p) => p.ticket === line.ticket)?.priceOpen ?? null;
  }
  const o = orders.find((x) => x.ticket === line.ticket);
  if (!o) return null;
  return line.kind === 'order'
    ? o.priceOrder
    : o.priceStopLimit > 0 && o.type.endsWith('StopLimit')
      ? o.priceStopLimit
      : o.priceOrder;
}

export const pointsBetween = (from: number, to: number, digits: number) =>
  Math.round((to - from) * 10 ** digits);

/** The order types a right-click at `price` can create: the ones that sit on that side of the market. */
export function pendingChoicesAt(price: number, bid: number, ask: number): OrderType[] {
  if (price > ask) return ['BuyStop', 'SellLimit'];
  if (price < bid) return ['BuyLimit', 'SellStop'];
  return [];
}

/** A position is a good target of a right-click "set SL/TP here" when the price is on the right side. */
export function stopTargetAt(position: Position, price: number): 'stopLoss' | 'takeProfit' {
  const profitSide = position.side === 'Buy' ? price > position.priceOpen : price < position.priceOpen;
  return profitSide ? 'takeProfit' : 'stopLoss';
}
